import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

// Import translations
import en from "@/locales/en.json";
import hi from "@/locales/hi.json";

type Locale = "en" | "hi";

type TranslationKeys = typeof en;

interface TranslationContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const translations: Record<Locale, TranslationKeys> = { en, hi };

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export function TranslationProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("leestone_locale") as Locale) || "en";
    }
    return "en";
  });

  useEffect(() => {
    localStorage.setItem("leestone_locale", locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
  };

  // Translation function - supports nested keys like "common.addToCart"
  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = translations[locale];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    if (typeof value === "string") {
      return value;
    }
    
    // Fallback to English
    value = translations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    
    return typeof value === "string" ? value : key;
  };

  return (
    <TranslationContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
}
